import { useOrganization, useUser } from "@clerk/expo";
import { Lock, X } from "lucide-react-native";
import {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Image, ScrollView } from "react-native";

import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  Box,
  Button,
  ButtonText,
  ErrorWithRetry,
  Heading,
  HStack,
  Input,
  InputField,
  Pressable,
  Skeleton,
  Text,
  VStack,
} from "@/components/ui";
import { DropdownPicker } from "@/components/ui/pickers";
import { useLocalization, useOrganizationMembers } from "@/hooks";
import API from "@/hooks/api";
import { SharePermission, ShareResourceType, User } from "@/types/resources";

/* ── Types ─────────────────────────────────────────────── */

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  ownerId: string;
  resourceId: string;
  resourceName: string;
  resourceType: ShareResourceType;
}

/* ── Member avatar ──────────────────────────────────────── */

const MemberAvatar: FC<{ imageUrl?: string; name: string }> = ({
  imageUrl,
  name,
}) => {
  const initials = name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <Box className="w-8 h-8 rounded-full bg-primary-100 items-center justify-center overflow-hidden shrink-0">
      {imageUrl ? (
        <Image
          source={{ uri: imageUrl }}
          style={{ width: 32, height: 32, borderRadius: 16 }}
        />
      ) : (
        <Text className="text-xs font-semibold text-primary-700">
          {initials}
        </Text>
      )}
    </Box>
  );
};

/* ── Member row ─────────────────────────────────────────── */

interface MemberRowProps {
  formatName: (user: User) => string;
  isCurrentUser?: boolean;
  isDisabled?: boolean;
  isExternal?: boolean;
  isOwner?: boolean;
  label?: string;
  member: User | undefined;
  onChangePermission?: (permission: SharePermission) => void;
  onRevoke?: () => void;
  permission?: SharePermission;
  userId: string;
}

const MemberRow: FC<MemberRowProps> = ({
  formatName,
  isCurrentUser = false,
  isDisabled = false,
  isExternal = false,
  isOwner = false,
  label,
  member,
  onChangePermission,
  onRevoke,
  permission,
  userId,
}) => {
  const { i18n } = useLocalization();

  const permissionItems = useMemo(
    () => [
      {
        label: i18n.t("sharing.viewPermission"),
        value: "view" as SharePermission,
      },
      {
        label: i18n.t("sharing.editPermission"),
        value: "edit" as SharePermission,
      },
    ],
    [i18n]
  );

  const baseName = member ? formatName(member) : userId;
  const displayName = isCurrentUser
    ? `${baseName} ${i18n.t("sharing.you")}`
    : baseName;

  return (
    <VStack className="gap-0.5">
      <HStack className="items-center gap-3 py-1.5">
        <MemberAvatar imageUrl={member?.imageUrl} name={displayName} />
        <VStack className="flex-1 gap-0">
          <HStack className="items-center gap-1.5">
            <Text
              className="text-sm text-typography-800 font-medium"
              numberOfLines={1}
            >
              {displayName}
            </Text>
            {isExternal && (
              <Box className="px-1.5 py-0.5 rounded-full bg-warning-100">
                <Text className="text-2xs text-warning-700">
                  {i18n.t("sharing.external")}
                </Text>
              </Box>
            )}
          </HStack>
          {member?.emailAddress && (
            <Text className="text-xs text-typography-400" numberOfLines={1}>
              {member.emailAddress}
            </Text>
          )}
        </VStack>
        {isOwner ? (
          <Text className="text-xs text-typography-400 font-medium">
            {i18n.t("sharing.owner")}
          </Text>
        ) : (
          <HStack className="items-center gap-1.5 shrink-0">
            <DropdownPicker
              className="w-[104px]"
              isDisabled={isDisabled}
              items={permissionItems}
              onChange={(val) => onChangePermission?.(val as SharePermission)}
              placeholder={i18n.t("sharing.viewPermission")}
              size="sm"
              value={permission ?? "view"}
            />
            {onRevoke && !isDisabled && (
              <Pressable
                className="p-1.5 rounded-lg hover:bg-background-100"
                onPress={onRevoke}
              >
                <X color="rgb(var(--color-typography-400))" size={14} />
              </Pressable>
            )}
          </HStack>
        )}
      </HStack>
      {label && (
        <Text className="text-xs text-typography-400 text-right">{label}</Text>
      )}
    </VStack>
  );
};

/* ── Section label ──────────────────────────────────────── */

const SectionLabel: FC<{ label: string }> = ({ label }) => (
  <Text className="text-md font-semibold text-typography-500 mb-1 mt-2">
    {label}
  </Text>
);

/* ── Skeleton ───────────────────────────────────────────── */

const ShareModalSkeleton: FC = () => (
  <VStack className="gap-0.5">
    <Skeleton className="h-2.5 w-36 rounded mt-2 mb-1" />
    <VStack className="gap-2 mt-2">
      {[1, 2].map((i) => (
        <HStack className="items-center gap-3" key={i}>
          <Skeleton className="w-8 h-8 rounded-full shrink-0" />
          <VStack className="flex-1 gap-1.5">
            <Skeleton className="h-3.5 w-32 rounded" />
            <Skeleton className="h-2.5 w-40 rounded" />
          </VStack>
          <Skeleton className="h-8 w-[104px] rounded-xl shrink-0" />
        </HStack>
      ))}
    </VStack>
  </VStack>
);

/* ── Search input ───────────────────────────────────────── */

/**
 * Uncontrolled search box.
 *
 * Earlier iterations tried debouncing + `React.memo`, but the caret
 * still jumped to the end whenever the user edited in the middle of
 * a word. The root cause is that React Native Web's `<TextInput>`
 * in controlled mode re-applies the `value` prop to the underlying
 * DOM `<input>` on every commit — and any commit that happens while
 * the user has the caret mid-string (Clerk hook updates, `useShares`
 * polling, `useSearchUsers` loading state, parent re-renders from
 * suggestion list updates, …) snaps the selection to the end of the
 * value.
 *
 * The fix is to keep the input itself **uncontrolled**: it manages
 * its own DOM value via `defaultValue`, and reports edits to the
 * parent through `onChangeText`. The parent's `searchQuery` still
 * drives the suggestion list and search API — it just doesn't try
 * to push the value back into the input.
 *
 * When we need to clear the field externally (after adding a
 * member, or when reopening the modal) we bump a `key` on this
 * component so React remounts the underlying `<input>` with an
 * empty `defaultValue`. That avoids any controlled-mode quirks
 * entirely.
 */
interface SearchInputProps {
  defaultValue?: string;
  onBlur: () => void;
  onChangeText: (text: string) => void;
  onFocus: () => void;
  placeholder: string;
}

const SearchInput: FC<SearchInputProps> = memo(function SearchInput({
  defaultValue,
  onBlur,
  onChangeText,
  onFocus,
  placeholder,
}) {
  return (
    <Input className="rounded-xl bg-background-50 border border-outline-200">
      <InputField
        defaultValue={defaultValue}
        onBlur={onBlur}
        onChangeText={onChangeText}
        onFocus={onFocus}
        placeholder={placeholder}
      />
    </Input>
  );
});

/* ── Main component ─────────────────────────────────────── */

const ShareModal: FC<ShareModalProps> = ({
  isOpen,
  onClose,
  ownerId,
  resourceId,
  resourceName,
  resourceType,
}) => {
  const { i18n } = useLocalization();
  const { user } = useUser();
  const currentUserId = user?.id;
  const { organization } = useOrganization();
  const { formatMemberName, users: orgMembers } = useOrganizationMembers();

  const permissionItems = useMemo(
    () => [
      {
        label: i18n.t("sharing.viewPermission"),
        value: "view" as SharePermission,
      },
      {
        label: i18n.t("sharing.editPermission"),
        value: "edit" as SharePermission,
      },
    ],
    [i18n]
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  /* Bumped to force-remount the uncontrolled `<SearchInput>` whenever
     we need to clear the field externally (after adding a member, on
     close, …). See the long comment above `SearchInput`. */
  const [searchResetKey, setSearchResetKey] = useState(0);
  const searchRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetSearchInput = useCallback(() => {
    setSearchQuery("");
    setSearchResetKey((k) => k + 1);
  }, []);

  /* Stable handlers so the memoized `<SearchInput>` does not re-render
     on every parent commit — see comment above `SearchInput`. */
  const handleSearchFocus = useCallback(() => {
    if (searchRef.current) clearTimeout(searchRef.current);
    setSearchFocused(true);
  }, []);
  const handleSearchBlur = useCallback(() => {
    // Delay to allow suggestion press to register before we unmount it.
    searchRef.current = setTimeout(() => setSearchFocused(false), 150);
  }, []);

  const { error, loading, refresh, setShares, shares } = API.Sharing.useShares(
    resourceId,
    resourceType
  );
  const { createSharing, error: createError } = API.Sharing.useCreateSharing();
  const { updateSharing, error: updateError } = API.Sharing.useUpdateSharing();
  const { deleteSharing, error: deleteError } = API.Sharing.useDeleteSharing();
  const {
    clear: clearSearchResults,
    results: searchResults,
    search: searchUsers,
  } = API.Sharing.useSearchUsers();

  const mutationError = createError ?? updateError ?? deleteError;

  /* Fire the user search once the query reaches 3 chars. The hook
     debounces the request (and enforces its own >= 3 guard), so we
     just call it directly here. Org-member suggestions below keep
     filtering live on every keystroke since they're local. */
  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 3) {
      clearSearchResults();
      return;
    }
    searchUsers(q);
  }, [searchQuery, searchUsers, clearSearchResults]);

  /* ── Derived data ── */

  const userShares = useMemo(() => {
    const direct = shares.filter((s) => !s.inherited && s.user?.id);
    const inherited = shares.filter((s) => s.inherited && s.user?.id);
    return [...direct, ...inherited];
  }, [shares]);

  const orgShare = useMemo(
    () => shares.find((s) => s.organization_id) ?? null,
    [shares]
  );

  const generalAccessItems = useMemo(
    () => [
      { label: i18n.t("sharing.restricted"), value: "restricted" },
      ...(organization
        ? [{ label: organization.name, value: organization.id }]
        : []),
    ],
    [i18n, organization]
  );

  const existingUserIds = useMemo(
    () => new Set([ownerId, ...shares.map((s) => s.user?.id).filter(Boolean)]),
    [ownerId, shares]
  );

  // Org members matching the query (name search)
  const suggestions = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return Object.entries(orgMembers)
      .filter(([userId]) => !existingUserIds.has(userId))
      .filter(([_, m]) =>
        `${m.firstName ?? ""} ${m.lastName ?? ""}`.toLowerCase().includes(q)
      )
      .map(([userId, m]) => ({ userId, ...m }));
  }, [searchQuery, orgMembers, existingUserIds]);

  // Past collaborators from search endpoint (filtered to exclude already-shared)
  const collaboratorSuggestions = useMemo(
    () => searchResults.filter((u) => !existingUserIds.has(u.id)),
    [searchResults, existingUserIds]
  );

  // Merge: org members first, then external collaborators (no duplicates)
  const allSuggestions = useMemo(() => {
    const orgUserIds = new Set(suggestions.map((s) => s.userId));
    const externalCollaborators = collaboratorSuggestions.filter(
      (c) => !orgUserIds.has(c.id)
    );
    return { external: externalCollaborators, orgMembers: suggestions };
  }, [suggestions, collaboratorSuggestions]);

  /* ── Handlers ── */

  const handleAddMember = useCallback(
    async (userId: string) => {
      resetSearchInput();
      setSearchFocused(false);
      clearSearchResults();
      const newShare = await createSharing({
        permission: orgShare?.permission ?? "view",
        resource_id: resourceId,
        resource_type: resourceType,
        user_id: userId,
      });
      if (!newShare) return;
      const localUser = orgMembers[userId];
      if (localUser) {
        setShares((prev) => [...prev, { ...newShare, user: localUser }]);
      } else {
        await refresh();
      }
    },
    [
      clearSearchResults,
      createSharing,
      orgMembers,
      orgShare?.permission,
      refresh,
      resetSearchInput,
      resourceId,
      resourceType,
      setShares,
    ]
  );

  const handleChangePermission = useCallback(
    async (
      shareId: string,
      permission: SharePermission,
      isInherited: boolean
    ) => {
      if (isInherited) {
        // Inherited share — create a direct override and replace the inherited record in state
        const existing = shares.find((s) => s.id === shareId);
        if (!existing) return;
        const share = await createSharing({
          permission,
          resource_id: resourceId,
          resource_type: resourceType,
          user_id: existing.user?.id ?? undefined,
          organization_id: existing.organization_id ?? undefined,
        });
        if (share)
          setShares((prev) => [...prev.filter((s) => s.id !== shareId), share]);
      } else {
        const updated = await updateSharing(shareId, permission);
        if (updated)
          setShares((prev) =>
            prev.map((s) => (s.id === shareId ? { ...s, permission } : s))
          );
      }
    },
    [createSharing, resourceId, resourceType, setShares, shares, updateSharing]
  );

  const handleRevoke = useCallback(
    async (shareId: string) => {
      const ok = await deleteSharing(shareId);
      if (ok) await refresh();
    },
    [deleteSharing, refresh]
  );

  const handleGeneralAccessChange = useCallback(
    async (value: string) => {
      if (value === "restricted") {
        if (orgShare && !orgShare.inherited) {
          const ok = await deleteSharing(orgShare.id);
          if (ok) await refresh();
        }
      } else {
        // If inherited, create a direct org share (overrides inheritance)
        const share = await createSharing({
          organization_id: value,
          permission: orgShare?.permission ?? "view",
          resource_id: resourceId,
          resource_type: resourceType,
        });
        if (share)
          setShares((prev) => [
            ...(orgShare ? prev.filter((s) => s.id !== orgShare.id) : prev),
            share,
          ]);
      }
    },
    [
      createSharing,
      deleteSharing,
      orgShare,
      refresh,
      resourceId,
      resourceType,
      setShares,
    ]
  );

  const handleOrgPermissionChange = useCallback(
    async (permission: string) => {
      if (!orgShare) return;
      await handleChangePermission(
        orgShare.id,
        permission as SharePermission,
        !!orgShare.inherited
      );
    },
    [handleChangePermission, orgShare]
  );

  const handleClose = useCallback(() => {
    resetSearchInput();
    setSearchFocused(false);
    clearSearchResults();
    onClose();
  }, [clearSearchResults, onClose, resetSearchInput]);

  /* ── Render ── */

  const ownerMember = orgMembers[ownerId];

  return (
    <AlertDialog isOpen={isOpen} onClose={handleClose}>
      <AlertDialogBackdrop />
      <AlertDialogContent className="gap-6 max-w-lg">
        <AlertDialogHeader>
          <Heading size="lg">
            {i18n.t("sharing.manageAccess", { name: resourceName })}
          </Heading>
        </AlertDialogHeader>

        <AlertDialogBody className="overflow-visible">
          <VStack className="gap-4 overflow-visible">
            {/* Search input — z-index wrapper so suggestions float above the list */}
            <Box className="relative z-10">
              <SearchInput
                defaultValue=""
                key={searchResetKey}
                onBlur={handleSearchBlur}
                onChangeText={setSearchQuery}
                onFocus={handleSearchFocus}
                placeholder={i18n.t("sharing.addPeople")}
              />

              {/* Suggestion dropdown */}
              {searchFocused &&
                (allSuggestions.orgMembers.length > 0 ||
                  allSuggestions.external.length > 0) && (
                  <Box className="absolute top-full left-0 right-0 mt-1 z-50 bg-background-0 border border-outline-200 rounded-xl shadow-soft-3 overflow-hidden">
                    {allSuggestions.orgMembers
                      .slice(0, 6)
                      .map(({ userId, ...member }) => (
                        <Pressable
                          className="px-3 py-2.5 hover:bg-background-50 active:bg-background-100"
                          key={userId}
                          onPress={() => handleAddMember(userId)}
                        >
                          <HStack className="items-center gap-3">
                            <MemberAvatar
                              imageUrl={member.imageUrl}
                              name={formatMemberName(member)}
                            />
                            <Text className="text-sm text-typography-800">
                              {formatMemberName(member)}
                            </Text>
                          </HStack>
                        </Pressable>
                      ))}
                    {allSuggestions.external.slice(0, 4).map((result) => {
                      const name =
                        formatMemberName(result) ||
                        result.emailAddress ||
                        result.id;
                      return (
                        <Pressable
                          className="px-3 py-2.5 hover:bg-background-50 active:bg-background-100"
                          key={result.id}
                          onPress={() => handleAddMember(result.id)}
                        >
                          <HStack className="items-center gap-3">
                            <MemberAvatar
                              imageUrl={result.imageUrl ?? undefined}
                              name={name}
                            />
                            <VStack className="flex-1 gap-0">
                              <HStack className="items-center gap-1.5">
                                <Text className="text-sm text-typography-800">
                                  {name}
                                </Text>
                                <Box className="px-1.5 py-0.5 rounded-full bg-warning-100">
                                  <Text className="text-2xs text-warning-700">
                                    {i18n.t("sharing.external")}
                                  </Text>
                                </Box>
                              </HStack>
                              {result.emailAddress && (
                                <Text className="text-xs text-typography-400">
                                  {result.emailAddress}
                                </Text>
                              )}
                            </VStack>
                          </HStack>
                        </Pressable>
                      );
                    })}
                  </Box>
                )}
            </Box>

            {/* Shares list */}
            {loading ? (
              <ShareModalSkeleton />
            ) : error ? (
              <ErrorWithRetry
                error={error}
                onRetry={refresh}
                title={i18n.t("sharing.errorLoading")}
              />
            ) : (
              <ScrollView
                className="max-h-72"
                showsVerticalScrollIndicator={false}
              >
                <VStack className="gap-0.5">
                  <SectionLabel label={i18n.t("sharing.directAccess")} />

                  <VStack className="gap-0.5 mt-2">
                    <MemberRow
                      formatName={formatMemberName}
                      isCurrentUser={ownerId === currentUserId}
                      isOwner
                      member={ownerMember}
                      permission="view"
                      userId={ownerId}
                    />
                    {userShares.map((share) => {
                      const userId = share.user?.id ?? "";
                      const member: User | undefined =
                        orgMembers[userId] ?? share.user ?? undefined;
                      return (
                        <MemberRow
                          formatName={formatMemberName}
                          isCurrentUser={userId === currentUserId}
                          isExternal={!orgMembers[userId]}
                          key={share.id}
                          label={
                            share.inherited
                              ? i18n.t("sharing.inheritedFrom", {
                                  name: share.inherited.name,
                                })
                              : undefined
                          }
                          member={member}
                          onChangePermission={(p) =>
                            handleChangePermission(
                              share.id,
                              p,
                              !!share.inherited
                            )
                          }
                          onRevoke={
                            share.inherited
                              ? undefined
                              : () => handleRevoke(share.id)
                          }
                          permission={share.permission}
                          userId={userId}
                        />
                      );
                    })}
                  </VStack>
                </VStack>
              </ScrollView>
            )}
            {/* General access */}
            {!loading && !error && (
              <VStack className="gap-0.5 mt-4">
                <SectionLabel label={i18n.t("sharing.generalAccess")} />
                <HStack className="items-center gap-3 mt-2">
                  <Box
                    className={`w-8 h-8 rounded-full items-center justify-center overflow-hidden shrink-0${orgShare && organization?.imageUrl ? "" : " bg-background-100"}`}
                  >
                    {orgShare && organization?.imageUrl ? (
                      <Image
                        source={{ uri: organization.imageUrl }}
                        style={{ width: 32, height: 32, borderRadius: 16 }}
                      />
                    ) : (
                      <Lock className="text-typography-500" size={15} />
                    )}
                  </Box>
                  <DropdownPicker
                    className="flex-1"
                    isDisabled={!!orgShare?.inherited}
                    items={generalAccessItems}
                    onChange={handleGeneralAccessChange}
                    placeholder={i18n.t("sharing.restricted")}
                    size="sm"
                    value={
                      orgShare
                        ? (organization?.id ?? "restricted")
                        : "restricted"
                    }
                  />
                  {orgShare && (
                    <DropdownPicker
                      className="w-[104px]"
                      items={permissionItems}
                      onChange={handleOrgPermissionChange}
                      placeholder={i18n.t("sharing.viewPermission")}
                      size="sm"
                      value={orgShare.permission}
                    />
                  )}
                </HStack>
                {orgShare?.inherited && (
                  <Text className="text-xs text-typography-400 mt-1 text-right">
                    {i18n.t("sharing.inheritedFrom", {
                      name: orgShare.inherited.name,
                    })}
                  </Text>
                )}
              </VStack>
            )}

            {mutationError && (
              <Text className="text-sm text-error-600 text-left">
                {mutationError}
              </Text>
            )}
          </VStack>
        </AlertDialogBody>

        <AlertDialogFooter className="pt-2">
          <HStack className="justify-end w-full">
            <Button
              className="rounded-xl"
              onPress={handleClose}
              variant="outline"
            >
              <ButtonText>{i18n.t("misc.close")}</ButtonText>
            </Button>
          </HStack>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ShareModal;
